/**
 * @file DecisionView.tsx
 * @description
 * Final IRB decision display with payment gating and document downloads.
 */

import { DocumentDownloadLink } from '../DocumentDownloadLink';
import React, { useEffect, useMemo, useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../hooks/useAuth';
import { SubmissionCapabilities, SubmissionRow } from '../../types/submission';
import { useNavigate } from 'react-router-dom';
import {
  ContinuingReviewEligibility,
  getContinuingReviewEligibility,
  initializeContinuingReview,
} from '../../services/continuingReviewService';
import { isContinuingReviewsUiEnabled } from '../../services/featureFlagService';
import { billingRoute, draftEditRoute, needsActionEditRoute, submissionDetailsRoute } from '../../utils/submissionRoutes';
import { formatDateOnly } from '../../utils/dateUtils';

/**
 * Interface representing a single row in the `review_decisions` table.
 */
interface ReviewDecisionRow {
  approval_date: string;
  id: string;
  submission_id: string;
  reviewer_id: string;
  decision: 'APPROVED' | 'COND_APPROVED' | 'DENIED';
  decision_data: string;
  created_at: string; // Timestamp of final decision
  expiry_date: string | null;
}

/**
 * DecisionViewProps
 * ---------------
 * Accepts a single `submission` object. We rely on submission.payment_completed
 * to check payment gating, plus submission.id to fetch from `review_decisions`.
 */
interface DecisionViewProps {
  capabilities: SubmissionCapabilities;
  submission: SubmissionRow;
}

/**
 * Interface for document metadata from the documents table
 */
interface DocumentMetadata {
  id: string;
  submission_id: string;
  user_id: string | null;
  filename: string;
  file_path: string;
  uploaded_at: string;
  type: string;
  is_active?: boolean;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  decision_letter: 'IRB Decision Letter',
  amendment_decision_letter: 'Amendment Decision Letter',
  pi_attest_doc_signed: 'PI Attestation (Signed)',
  submission_form_pdf: 'Submission Form',
  doc_protocol: 'Study Protocol',
  doc_consent_form: 'Informed Consent Form',
  stamped_icf: 'Stamped Informed Consent Form (ICF)',
  doc_human_subject_training: 'Human Subject Training Documentation',
  doc_ind_fda_letter: 'FDA IND / Expanded-Access Correspondence',
  doc_irb_auth_agreement: 'IRB Authorization Agreement',
  doc_administrative_action: 'Administrative Action Documentation',
  doc_study_personnel_resumes: 'Study Personnel Resumes/CVs',
  doc_investigator_brochure: "Investigator's Brochure",
  doc_recruitment_materials: 'Participant Recruitment Materials',
  doc_data_collection_tools: 'Data Collection Tools',
  doc_additional_files: 'Additional Files',
  doc_fwa_documentation: 'Federal Wide Assurance (FWA) Documentation',
};

const OFFICIAL_DOC_TYPES = new Set<string>(['decision_letter', 'amendment_decision_letter', 'pi_attest_doc_signed']);
const HIDDEN_DOC_TYPES = new Set<string>(['submission_form_markdown', 'pi_attest_doc_unsigned']);
const formatDate = (dateString: string) => {
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown date';
  }
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * DecisionView
 * ------------
 * Renders the final IRB decision details if payment is complete. Otherwise, displays a gating message.
 */
const DecisionView: React.FC<DecisionViewProps> = ({ submission, capabilities }) => {
  const [reviewDecision, setReviewDecision] = useState<ReviewDecisionRow | null>(null);
  const [loadingDecision, setLoadingDecision] = useState<boolean>(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);

  const [documents, setDocuments] = useState<DocumentMetadata[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);

  const [crEligibility, setCrEligibility] = useState<ContinuingReviewEligibility | null>(null);
  const [supersededByCrId, setSupersededByCrId] = useState<string | null>(null);
  const [crError, setCrError] = useState<string | null>(null);
  const [crStarting, setCrStarting] = useState<boolean>(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const supabase = useSupabase();

  // Owner-only, type-gated, flag-gated entry point for continuing-review controls.
  // Shared readers and non-renewable types never probe.
  const crGateOpen =
    submission.payment_completed &&
    isContinuingReviewsUiEnabled() &&
    (submission.submission_type === 'NEW_SUBMISSION' || submission.submission_type === 'CONTINUING_REVIEW') &&
    capabilities.canStartContinuingReview;

  const handleViewLockedSubmission = () => {
    navigate(`/view-submission?submissionId=${submission.id}`);
  };

  useEffect(() => {
    if (!submission.payment_completed) {
      return;
    }

    const fetchDecision = async () => {
      setLoadingDecision(true);
      setDecisionError(null);

      try {
        const { data, error } = await supabase
          .from('review_decisions')
          .select('*')
          .eq('submission_id', submission.id)
          .single();

        if (error) {
          console.error('Error fetching review decision:', error);
          setReviewDecision(null);
          setDecisionError('Error fetching final decision from the database.');
        } else if (!data) {
          setReviewDecision(null);
          setDecisionError('No final decision found for this submission.');
        } else {
          setReviewDecision(data as ReviewDecisionRow);
        }
      } catch (err: any) {
        console.error('Unexpected error while fetching decision:', err);
        setReviewDecision(null);
        setDecisionError('Unexpected error while fetching final decision.');
      } finally {
        setLoadingDecision(false);
      }
    };

    fetchDecision();
  }, [submission.id, submission.payment_completed, supabase]);

  useEffect(() => {
    if (!submission.payment_completed || !user) {
      return;
    }

    const fetchDocuments = async () => {
      setLoadingDocuments(true);
      setDocumentsError(null);

      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('submission_id', submission.id)
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching documents:', error);
          setDocuments([]);
          setDocumentsError('Could not fetch documents for this submission.');
        } else {
          setDocuments((data as DocumentMetadata[]) || []);
        }
      } catch (err: any) {
        console.error('Unexpected error fetching documents:', err);
        setDocuments([]);
        setDocumentsError('Unexpected error while fetching documents.');
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, [submission.id, submission.payment_completed, supabase, user]);



  // Probe continuing-review eligibility once the owner gate is open. Depends only
  // on primitives (crGateOpen, submission.id) + the stable supabase client so it
  // does not re-run on every render.
  useEffect(() => {
    if (!crGateOpen) {
      setCrEligibility(null);
      return;
    }

    let cancelled = false;
    const fetchEligibility = async () => {
      const result = await getContinuingReviewEligibility(supabase, submission.id);
      if (!cancelled) {
        setCrEligibility(result);
      }
    };

    fetchEligibility();
    return () => {
      cancelled = true;
    };
  }, [crGateOpen, submission.id, supabase]);

  // When this approval has been superseded by a decided continuing review,
  // find that CR so the banner can link to the current approval. RLS scopes
  // the query: owners see their own CRs; shared readers get nothing (and never
  // reach here anyway because crGateOpen requires owner capabilities).
  useEffect(() => {
    if (!crGateOpen || crEligibility?.queue_state !== 'SUPERSEDED') {
      setSupersededByCrId(null);
      return;
    }

    let cancelled = false;
    const fetchSupersedingCr = async () => {
      const { data, error } = await supabase
        .from('submissions')
        .select('id, created_at, review_decisions!inner(decision)')
        .eq('parent_submission_id', submission.id)
        .eq('submission_type', 'CONTINUING_REVIEW')
        .eq('status', 'DECISION_ISSUED')
        .order('created_at', { ascending: false });

      if (cancelled) return;
      if (error) {
        console.error('Error finding superseding continuing review:', error);
        return;
      }

      // PostgREST embeds review_decisions as an object (unique submission_id)
      // but be tolerant of array shape; pick the newest approving decision.
      const isApproving = (d: any) => d?.decision === 'APPROVED' || d?.decision === 'COND_APPROVED';
      const match = (data ?? []).find((row: any) => {
        const rd = row.review_decisions;
        return Array.isArray(rd) ? rd.some(isApproving) : isApproving(rd);
      });
      setSupersededByCrId(match?.id ?? null);
    };

    fetchSupersedingCr();
    return () => {
      cancelled = true;
    };
  }, [crGateOpen, crEligibility?.queue_state, submission.id, supabase]);

  const handleStartContinuingReview = async () => {
    setCrError(null);
    setCrStarting(true);
    try {
      const result = await initializeContinuingReview(supabase, submission.id);
      navigate(draftEditRoute(result.submission_id, 'CONTINUING_REVIEW'));
    } catch (err: any) {
      setCrError(err?.message || 'Unable to start continuing review.');
    } finally {
      setCrStarting(false);
    }
  };

  const handleContinueContinuingReview = () => {
    const openId = crEligibility?.open_cr_submission_id;
    if (!openId) {
      return;
    }
    if (crEligibility?.open_cr_status === 'NEEDS_ACTION') {
      navigate(needsActionEditRoute(openId, 'CONTINUING_REVIEW'));
    } else {
      navigate(draftEditRoute(openId, 'CONTINUING_REVIEW'));
    }
  };

  const decisionStatus = reviewDecision?.decision;
  const { officialDocs, stampedConsentDocs, studyDocs } = useMemo(() => {
    const visibleDocs = documents
      .filter((doc) => !HIDDEN_DOC_TYPES.has(doc.type))
      .filter((doc) => {
        if (doc.type === 'stamped_icf') {
          return decisionStatus === 'APPROVED';
        }
        if (doc.type === 'doc_consent_form') {
          return decisionStatus !== 'APPROVED';
        }
        return true;
      });
    const official = visibleDocs
      .filter((doc) => OFFICIAL_DOC_TYPES.has(doc.type))
      .sort((a, b) => {
        const order: Record<string, number> = {
          decision_letter: 0,
          amendment_decision_letter: 1,
          pi_attest_doc_signed: 2,
        };
        return (order[a.type] ?? 99) - (order[b.type] ?? 99);
      });

    const stamped = visibleDocs
      .filter((doc) => doc.type === 'stamped_icf')
      .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime());

    const study = visibleDocs
      .filter((doc) => !OFFICIAL_DOC_TYPES.has(doc.type))
      .filter((doc) => doc.type !== 'stamped_icf')
      .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime());

    return { officialDocs: official, stampedConsentDocs: stamped, studyDocs: study };
  }, [decisionStatus, documents]);

  const renderContinuingReviewSection = () => {
    // Link back to the originating study when viewing a continuing review's own decision.
    const parentId = submission.parent_submission_id;
    const sourceLink =
      submission.submission_type === 'CONTINUING_REVIEW' && parentId ? (
        <button
          type="button"
          onClick={() => navigate(submissionDetailsRoute(parentId))}
          className="text-sm text-blue-600 hover:underline"
        >
          View the original study submission
        </button>
      ) : null;

    // Respect the build-time gate, the eligibility probe, and the server-side CTA flag.
    if (!crGateOpen || !crEligibility || !crEligibility.user_cta_enabled) {
      return sourceLink;
    }

    const { queue_state, can_start, blocked_reason, expiry_date, open_cr_submission_id, open_cr_status } =
      crEligibility;

    const startButton = (
      <button
        type="button"
        onClick={handleStartContinuingReview}
        disabled={crStarting}
        className={`px-4 py-2 rounded text-sm text-white ${crStarting ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#F55D3E] hover:bg-[#F9A594]'}`}
      >
        {crStarting ? 'Starting…' : 'Start Continuing Review'}
      </button>
    );

    let controls: React.ReactNode = null;

    if (open_cr_submission_id) {
      if (open_cr_status === 'IRB_REVIEW') {
        controls = (
          <div className="space-y-2">
            <p className="text-gray-700">A continuing review for this study is pending with the IRB.</p>
            <button
              type="button"
              onClick={() => navigate(submissionDetailsRoute(open_cr_submission_id))}
              className="text-sm text-blue-600 hover:underline"
            >
              View the pending continuing review
            </button>
          </div>
        );
      } else if (open_cr_status === 'DRAFT' || open_cr_status === 'NEEDS_ACTION') {
        controls = (
          <div className="space-y-2">
            <p className="text-gray-700">You have a continuing review in progress for this study.</p>
            <button
              type="button"
              onClick={handleContinueContinuingReview}
              className="px-4 py-2 rounded text-sm text-white bg-[#F55D3E] hover:bg-[#F9A594]"
            >
              Continue Continuing Review
            </button>
          </div>
        );
      }
    } else if (queue_state === 'SUPERSEDED') {
      controls = (
        <div className="border rounded-lg p-4 bg-green-50 border-green-300 text-green-900 space-y-2">
          <p className="font-semibold">This approval has been renewed.</p>
          <p className="text-sm">
            A continuing review for this study has been approved, and this page now reflects a prior
            approval period. The current decision letter, approval dates, and stamped consent forms are
            on the renewed approval.
          </p>
          {supersededByCrId && (
            <button
              type="button"
              onClick={() => navigate(submissionDetailsRoute(supersededByCrId))}
              className="px-4 py-2 rounded text-sm text-white bg-green-700 hover:bg-green-600"
            >
              View current approval →
            </button>
          )}
        </div>
      );
    } else if (queue_state === 'LAPSED') {
      controls = (
        <div className="border rounded-lg p-4 bg-amber-50 border-amber-300 text-amber-900 space-y-2">
          <p className="font-semibold">
            This study's approval expired{expiry_date ? ` on ${formatDateOnly(expiry_date)}` : ''}.
          </p>
          <p className="text-sm">
            Research activities must pause until the IRB re-approves the study. Start a continuing review to
            request re-approval.
          </p>
          {can_start && startButton}
        </div>
      );
    } else if (can_start) {
      controls = (
        <div className="space-y-2">
          <p className="text-gray-700">This study is due for continuing review.</p>
          {startButton}
        </div>
      );
    } else if (blocked_reason) {
      controls = (
        <div className="space-y-1">
          {expiry_date && (
            <p className="text-gray-700">Approval is current through {formatDateOnly(expiry_date)}.</p>
          )}
          <p className="text-sm text-gray-500">{blocked_reason}</p>
        </div>
      );
    }

    if (!controls && !sourceLink) {
      return null;
    }

    return (
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200 px-4 py-3 font-semibold text-gray-800">Continuing Review</div>
        <div className="p-4 space-y-3">
          {controls}
          {crError && <p className="text-sm text-red-600">{crError}</p>}
          {sourceLink}
        </div>
      </div>
    );
  };

  if (!submission.payment_completed) {
    return (
      <div>
        <p className="text-gray-700 mb-4">
          This submission has an IRB decision, but you must complete payment before you can view
          the final decision details or access documents.
        </p>
        {capabilities.canCompletePayment ? (
          <div className="space-y-3">
            <p className="text-gray-500">
              Once payment is confirmed, come back here to see the final IRB decision and documents.
            </p>
            <button
              type="button"
              onClick={() => navigate(billingRoute(submission.id))}
              className="px-4 py-2 rounded text-sm text-white bg-[#B83EF5] hover:bg-[#9c32c7]"
            >
              Complete Payment
            </button>
          </div>
        ) : (
          <p className="text-gray-500">
            Payment is not available for this submission.
          </p>
        )}
      </div>
    );
  }

  if (loadingDecision) {
    return (
      <div>
        <p className="text-gray-700">Loading the final decision details. Please wait...</p>
      </div>
    );
  }

  if (decisionError) {
    return (
      <div className="p-8">
        <h2 className="text-xl font-bold mb-4">IRB Decision Issued</h2>
        <p className="text-gray-700 mb-4">
          We were unable to retrieve a final decision record for this submission.
        </p>
        <p className="text-sm text-red-600 italic">{decisionError}</p>
      </div>
    );
  }

  if (reviewDecision) {
    const { decision, created_at } = reviewDecision;
    const displayDate = new Date(created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const bannerTitle =
      decision === 'APPROVED'
        ? 'Your study is approved.'
        : decision === 'COND_APPROVED'
          ? 'Your study is conditionally approved.'
          : 'The IRB decision has been issued.';
    const bannerTone =
      decision === 'DENIED'
        ? 'bg-amber-50 border-amber-200 text-amber-900'
        : 'bg-emerald-50 border-emerald-200 text-emerald-900';

    return (
      <div className="space-y-6">
        <div className={`border rounded-lg p-4 ${bannerTone}`}>
          <p className="text-lg font-semibold">{bannerTitle}</p>
          <p className="text-sm mt-1">
            <span className="font-medium">Decision:</span> {decision} |{' '}
            <span className="font-medium">Date:</span> {displayDate}
          </p>
          {isContinuingReviewsUiEnabled() &&
            (reviewDecision.approval_date || reviewDecision.expiry_date) && (
              <p className="text-sm mt-1">
                {reviewDecision.approval_date && (
                  <>
                    <span className="font-medium">Approval date:</span> {formatDateOnly(reviewDecision.approval_date)}
                  </>
                )}
                {reviewDecision.approval_date && reviewDecision.expiry_date && ' | '}
                {reviewDecision.expiry_date && (
                  <>
                    <span className="font-medium">Expiry date:</span> {formatDateOnly(reviewDecision.expiry_date)}
                  </>
                )}
              </p>
            )}
        </div>

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
          <div className="prose max-w-none text-gray-700">
            <ReactMarkdown>{reviewDecision.decision_data}</ReactMarkdown>
          </div>
        </div>

        {renderContinuingReviewSection()}

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
          <div className="border-b border-gray-200 px-4 py-3 font-semibold text-gray-800">
            Official Decision Documents
          </div>
          <div className="p-4 space-y-3">
            {loadingDocuments ? (
              <p className="text-sm text-gray-600">Loading documents...</p>
            ) : officialDocs.length === 0 ? (
              <p className="text-sm text-gray-500">No official decision documents available.</p>
            ) : (
              officialDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between border border-gray-200 rounded-md p-3">
                  <div>
                    <p className="text-gray-900 font-medium">
                      {DOCUMENT_TYPE_LABELS[doc.type] || doc.filename || doc.type}
                    </p>
                    <p className="text-xs text-gray-500">Uploaded: {formatDate(doc.uploaded_at)}</p>
                    {doc.filename && DOCUMENT_TYPE_LABELS[doc.type] && (
                      <p className="text-xs text-gray-400">{doc.filename}</p>
                    )}
                  </div>
                  <DocumentDownloadLink document={doc} client={supabase} />
                </div>
              ))
            )}
            {documentsError && (
              <p className="text-sm text-red-600">{documentsError}</p>
            )}
          </div>
        </div>

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
          <div className="border-b border-gray-200 px-4 py-3 font-semibold text-gray-800">
            IRB-Approved & Stamped Consent Forms
          </div>
          <div className="p-4 space-y-3">
            {loadingDocuments ? (
              <p className="text-sm text-gray-600">Loading documents...</p>
            ) : stampedConsentDocs.length === 0 ? (
              <p className="text-sm text-gray-500">No stamped consent forms available.</p>
            ) : (
              stampedConsentDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between border border-gray-200 rounded-md p-3">
                  <div>
                    <p className="text-gray-900 font-medium">
                      {DOCUMENT_TYPE_LABELS[doc.type] || doc.filename || doc.type}
                    </p>
                    <p className="text-xs text-gray-500">Uploaded: {formatDate(doc.uploaded_at)}</p>
                    {doc.filename && DOCUMENT_TYPE_LABELS[doc.type] && (
                      <p className="text-xs text-gray-400">{doc.filename}</p>
                    )}
                  </div>
                  <DocumentDownloadLink document={doc} client={supabase} />
                </div>
              ))
            )}
            {documentsError && (
              <p className="text-sm text-red-600">{documentsError}</p>
            )}
          </div>
        </div>

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
          <div className="border-b border-gray-200 px-4 py-3 font-semibold text-gray-800">
            Study Documents
          </div>
          <div className="p-4 space-y-3">
            {loadingDocuments ? (
              <p className="text-sm text-gray-600">Loading documents...</p>
            ) : studyDocs.length === 0 ? (
              <p className="text-sm text-gray-500">No study documents available.</p>
            ) : (
              studyDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between border border-gray-200 rounded-md p-3">
                  <div>
                    <p className="text-gray-900 font-medium">
                      {DOCUMENT_TYPE_LABELS[doc.type] || doc.filename || doc.type}
                    </p>
                    <p className="text-xs text-gray-500">Uploaded: {formatDate(doc.uploaded_at)}</p>
                    {doc.filename && DOCUMENT_TYPE_LABELS[doc.type] && (
                      <p className="text-xs text-gray-400">{doc.filename}</p>
                    )}
                  </div>
                  <DocumentDownloadLink document={doc} client={supabase} />
                </div>
              ))
            )}
            {documentsError && (
              <p className="text-sm text-red-600">{documentsError}</p>
            )}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="font-semibold text-lg mb-2">View Submission (Read-Only)</h3>
          <p className="text-gray-700 mb-3">
            If you wish to review the submission details in a read-only format, click below:
          </p>
          <button
            onClick={handleViewLockedSubmission}
            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
          >
            View Locked Submission
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h2 className="text-xl font-bold mb-4">IRB Decision Issued</h2>
      <p className="text-gray-700">
        We couldn't find a final review decision for this submission, even though payment is complete.
        This is unusual. Please contact support if you believe this is an error.
      </p>
    </div>
  );
};

export default DecisionView;
